import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';

export default function CrossPortalUsers() {
  const [users, setUsers] = useState([]);
  const [portals, setPortals] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [portalFilter, setPortalFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ username: '', email: '', password: '', role: 'user', portalId: '' });

  const fetchUsers = () => {
    const params = { page, search, portalId: portalFilter };
    api.get('/superadmin/users', { params }).then(({ data }) => {
      setUsers(data.users); setTotalPages(data.totalPages); setLoading(false);
    }).catch(() => { toast.error('Failed'); setLoading(false); });
  };

  useEffect(() => { fetchUsers(); }, [page, search, portalFilter]);
  useEffect(() => {
    api.get('/superadmin/portals').then(({ data }) => setPortals(Array.isArray(data.portals) ? data.portals : Array.isArray(data) ? data : []))
      .catch(() => setPortals([]));
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/superadmin/users', form);
      toast.success('User created');
      setShowModal(false); fetchUsers();
      setForm({ username: '', email: '', password: '', role: 'user', portalId: '' });
    } catch (err) { toast.error(err.response?.data?.msg || 'Failed'); }
  };

  const toggleStatus = async (user) => {
    try {
      await api.put(`/superadmin/users/${user._id}/status`, { status: user.status === 'active' ? 'inactive' : 'active' });
      toast.success('Status updated');
      fetchUsers();
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Cross-Portal Users</h1>
          <p className="page-subtitle">Manage users across all portals</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ New User</button>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <input type="text" className="form-input" placeholder="Search users..." style={{ flex: 1 }}
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          <select className="form-select" style={{ width: 200 }} value={portalFilter}
            onChange={e => { setPortalFilter(e.target.value); setPage(1); }}>
            <option value="">All Portals</option>
            {(Array.isArray(portals) ? portals : []).map(p => <option key={p._id} value={p.portalId}>{p.name}</option>)}
          </select>
        </div>
      </div>

      <div className="card">
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr><th>Username</th><th>Email</th><th>Role</th><th>Portal</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u._id}>
                  <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{u.username}</td>
                  <td>{u.email || '-'}</td>
                  <td><span className={`badge ${u.role === 'admin' ? 'badge-active' : 'badge-info'}`}>{u.role}</span></td>
                  <td>{u.portalId || 'default'}</td>
                  <td><span className={`badge badge-${u.status}`}>{u.status}</span></td>
                  <td>
                    <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 12px' }}
                      onClick={() => toggleStatus(u)}>
                      {u.status === 'active' ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan="6" style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)' }}>No users found</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button className="btn btn-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</button>
            <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>Page {page} of {totalPages}</span>
            <button className="btn btn-secondary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Create User</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Username</label>
                  <input type="text" className="form-input" value={form.username}
                    onChange={e => setForm({ ...form, username: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input type="email" className="form-input" value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Password</label>
                  <input type="password" className="form-input" value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select className="form-select" value={form.role}
                    onChange={e => setForm({ ...form, role: e.target.value })}>
                    <option value="admin">Admin</option>
                    <option value="user">User</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Portal</label>
                  <select className="form-select" value={form.portalId}
                    onChange={e => setForm({ ...form, portalId: e.target.value })} required>
                    <option value="">Select Portal</option>
                    {(Array.isArray(portals) ? portals : []).map(p => <option key={p._id} value={p.portalId}>{p.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
